(() => {
  'use strict';
  if (!window.customElements || customElements.get('km-cart-product-panel')) return;

  const events = ['cart:updated', 'cart:rendered', 'ajaxProduct:added', 'drawerOpen.CartDrawer'];
  const isSelectable = item => {
    const properties = item.properties || {};
    return !properties._km_bogo_activity && String(properties._km_cart_gift) !== 'true'
      && String(properties._uc_is_gift || '').trim() !== '1' && !properties._uc_pick_section;
  };
  const variantId = item => String(item.variant_id || item.id || '');
  const contextKey = item => JSON.stringify([
    String(item.product_id),
    Object.entries(item.properties || {}).sort(([left], [right]) => left.localeCompare(right)),
    String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '')
  ]);

  class KMCartProductPanel extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('#CartDrawer');
      if (!this.drawer) return;
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.media = window.matchMedia('(min-width: 769px)');
      this.revision = (this.revision || 0) + 1;
      this.onUpdate = event => {
        if (event.detail?.location && event.detail.location !== 'cart-drawer') return;
        const renderedEmpty = event.type === 'cart:rendered' && this.drawer.classList.contains('is-empty')
          && this.drawer.querySelector('[data-products] .cart__items')?.dataset.count === '0';
        const snapshot = window.KMCartState && event.type === 'cart:updated' ? null : event.detail?.cart;
        this.refresh(snapshot || (renderedEmpty ? this.emptyCart() : null));
      };
      this.onClick = event => this.selectFromClick(event);
      this.onChange = event => {
        const select = event.target.closest('[data-km-cart-variant-select]');
        if (select?.dataset.kmCartVariantKey === this.selection?.key) {
          this.pendingVariant = select.value;
          this.pendingSelect = select;
        }
      };
      this.onMedia = () => { if (this.cart) this.render(this.cart); };
      events.forEach(name => document.addEventListener(name, this.onUpdate));
      window.addEventListener('pageshow', this.onUpdate);
      this.drawer.addEventListener('click', this.onClick);
      this.drawer.addEventListener('change', this.onChange);
      this.media.addEventListener('change', this.onMedia);
      this.drawerEmpty = this.drawer.classList.contains('is-empty');
      this.stateObserver = new MutationObserver(() => {
        const empty = this.drawer.classList.contains('is-empty');
        if (empty === this.drawerEmpty) return;
        this.drawerEmpty = empty;
        this.refresh(empty ? this.emptyCart() : null);
      });
      this.stateObserver.observe(this.drawer, {attributes: true, attributeFilter: ['class']});
      this.refresh();
    }

    disconnectedCallback() {
      this.revision++;
      this.controller?.abort();
      events.forEach(name => document.removeEventListener(name, this.onUpdate));
      window.removeEventListener('pageshow', this.onUpdate);
      this.drawer?.removeEventListener('click', this.onClick);
      this.drawer?.removeEventListener('change', this.onChange);
      this.media?.removeEventListener('change', this.onMedia);
      this.stateObserver?.disconnect();
      this.pendingSelect = null;
    }

    emptyCart() {
      return {items: [], item_count: 0, total_price: 0, total_discount: 0};
    }

    async refresh(snapshot) {
      const revision = ++this.revision;
      this.controller?.abort();
      try {
        let cart = snapshot;
        if (!Array.isArray(cart?.items) && window.KMCartState) cart = await window.KMCartState.read(this.root + 'cart.js');
        else if (!Array.isArray(cart?.items)) {
          this.controller = new AbortController();
          const response = await fetch(this.root + 'cart.js', {
            credentials: 'same-origin', cache: 'no-store', signal: this.controller.signal
          });
          if (!response.ok) throw new Error('Unable to load cart.');
          cart = await response.json();
        }
        if (revision !== this.revision || !this.isConnected || !Array.isArray(cart.items)) return;
        this.cart = cart;
        this.render(cart);
      } catch (error) {
        if (revision === this.revision && error.name !== 'AbortError') this.dataset.state = 'unavailable';
      }
    }

    selectFromClick(event) {
      if (!this.media.matches || event.defaultPrevented || event.button > 0
        || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const line = event.target.closest('[data-km-cart-line]');
      if (!line || line.hasAttribute('data-km-gift-line-item') || !this.drawer.contains(line)) return;
      const pairToggle = event.target.closest('[data-km-cart-pair-toggle]');
      if (!pairToggle && event.target.closest('button, input, select, textarea, label, [contenteditable], [role="button"], .js-qty__wrapper, .cart__remove, .km-cart-line-item__options')) return;
      const link = event.target.closest('a, [role="link"]');
      if (link) return;
      const item = this.cart?.items.find(item => item.key === line.dataset.key && isSelectable(item));
      if (!item) return;
      const anchorChanged = this.selection?.key !== item.key;
      event.preventDefault();
      this.pendingVariant = null;
      this.pendingSelect = null;
      this.selection = {key: item.key, contextKey: contextKey(item), variantId: variantId(item)};
      this.render(this.cart);
      this.scrollTop = 0;
      if (anchorChanged) window.KMCartAnalytics?.trackProduct('select_cart_anchor_item', line);
    }

    render(cart) {
      const items = cart.items.filter(isSelectable);
      const previous = this.selection;
      const originalLines = items.filter(item => contextKey(item) === previous?.contextKey && variantId(item) === previous?.variantId);
      const originalLine = originalLines.length === 1 ? originalLines[0] : null;
      if (this.pendingVariant && this.pendingSelect?.dataset.kmVariantPending !== 'true'
        && this.pendingSelect?.value !== this.pendingVariant) {
        this.pendingVariant = null;
        this.pendingSelect = null;
      }
      const desiredVariant = this.pendingVariant || previous?.variantId;
      const desiredLines = items.filter(item => contextKey(item) === previous?.contextKey && variantId(item) === desiredVariant);
      const selected = items.find(item => item.key === previous?.key)
        || originalLine
        || (desiredLines.length === 1 ? desiredLines[0] : null)
        || items[0] || null;
      this.selection = selected ? {key: selected.key, contextKey: contextKey(selected), variantId: variantId(selected)} : null;
      if (!selected || variantId(selected) === this.pendingVariant) {
        this.pendingVariant = null;
        this.pendingSelect = null;
      }
      this.dataset.state = selected ? 'selected' : 'empty';
      const summary = this.querySelector('[data-km-selected-product]');
      const pair = this.querySelector('[data-km-cart-pair]');
      if (summary) {
        summary.hidden = false;
        const card = summary.querySelector('[data-km-selected-product-card]');
        const empty = summary.querySelector('[data-km-selected-product-empty]');
        if (card) card.hidden = !selected;
        if (empty) empty.hidden = Boolean(selected);
      }
      if (pair) pair.hidden = !selected;
      this.drawer.querySelectorAll('[data-km-cart-checkout-count]').forEach(node => {
        node.textContent = String(cart.item_count ?? cart.items.reduce((count, item) => count + Number(item.quantity || 0), 0));
      });
      this.drawer.querySelectorAll('[data-km-cart-line]').forEach(line => {
        const active = line.dataset.key === selected?.key;
        line.classList.toggle('km-cart-line-item--selected', active);
        const arrow = line.querySelector('[data-km-cart-selection-arrow]');
        if (arrow) arrow.hidden = !active || !this.media.matches;
        line.querySelectorAll('.cart__image a, .cart__item-name').forEach(link => {
          if (active && this.media.matches) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      });
      if (selected && summary) this.renderSummary(summary, selected);
      if (pair) {
        pair.dataset.productId = selected ? String(selected.product_id) : '';
        pair.dataset.variantId = selected ? variantId(selected) : '';
        pair.dispatchEvent(new CustomEvent('km:cart-pair-context', {
          bubbles: true, detail: {productId: selected?.product_id ?? null, variantId: selected ? variantId(selected) : null, item: selected}
        }));
      }
      this.dispatchEvent(new CustomEvent('km:cart-selection', {bubbles: true, detail: {cart, item: selected}}));
    }

    money(value) {
      if (window.theme?.Currency && window.theme.settings?.moneyFormat) {
        return window.theme.Currency.formatMoney(value, window.theme.settings.moneyFormat);
      }
      return '$' + (Number(value || 0) / 100).toFixed(2);
    }

    renderSummary(summary, item) {
      summary.dataset.kmProductId = String(item.product_id || '');
      summary.dataset.kmCartVariantId = String(item.variant_id || item.id || '');
      summary.dataset.kmProductHandle = item.handle || '';
      summary.dataset.kmProductTitle = item.product_title || item.title || '';
      const title = summary.querySelector('[data-km-selected-product-title]');
      title.textContent = item.product_title || item.title || '';
      const url = item.url || '#';
      title.setAttribute('href', url);
      summary.querySelector('[data-km-selected-product-link]').setAttribute('href', url);
      const image = summary.querySelector('[data-km-selected-product-image]');
      const imageUrl = typeof item.image === 'string' ? item.image : item.image?.url || item.image?.src;
      image.hidden = !imageUrl;
      if (imageUrl) image.src = imageUrl;
      else image.removeAttribute('src');
      image.alt = item.product_title || item.title || '';
      const options = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      summary.querySelector('[data-km-selected-product-options]').textContent = [options, 'Qty: ' + item.quantity].filter(Boolean).join(' · ');
      const line = [...this.drawer.querySelectorAll('[data-km-cart-line]')].find(line => line.dataset.key === item.key);
      const regular = Math.max(Number(item.original_price || 0), Number(line?.dataset.kmCartComparePrice || 0));
      const price = Number(item.final_price ?? item.price ?? 0);
      summary.querySelector('[data-km-selected-product-price]').textContent = this.money(price);
      const compare = summary.querySelector('[data-km-selected-product-compare]');
      compare.hidden = regular <= price;
      compare.textContent = regular > price ? this.money(regular) : '';
    }
  }
  customElements.define('km-cart-product-panel', KMCartProductPanel);
})();
